import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { localizeCategories, localizeProducts } from "@/lib/catalogLocalization";
import { localizePublicSettings } from "@/lib/publicPageLocalization";

async function safePublicGet(path, fallbackValue) {
  try {
    const { data } = await api.get(path);
    return data ?? fallbackValue;
  } catch {
    return fallbackValue;
  }
}

export function usePublicSettings() {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => safePublicGet("/settings", {}),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizePublicSettings(query.data || {}, language), [query.data, language]),
  };
}

export function usePublicCategories() {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey: ["public-categories"],
    queryFn: async () => safePublicGet("/categories", []),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizeCategories(query.data || [], language), [query.data, language]),
  };
}

export function usePublicCertificates() {
  return useQuery({
    queryKey: ["public-certificates"],
    queryFn: async () => safePublicGet("/certificates", []),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function usePublicFeaturedProducts() {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey: ["featured-products"],
    queryFn: async () => safePublicGet("/products?featured=true", []),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizeProducts(query.data || [], language), [query.data, language]),
  };
}

export function usePublicProducts(queryKey, path) {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey,
    queryFn: async () => safePublicGet(path, []),
    staleTime: 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizeProducts(query.data || [], language), [query.data, language]),
  };
}
